/**
 * Phase 8.2 — Admin V5 Tutor Learning Usage Log.
 * Visible under the Learning tab. Read-only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Users, Loader2, Filter, ClipboardCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (_e) { return iso; } };

const AdminTutorLearningUsagePanel = () => {
  const [logs, setLogs] = useState([]);
  const [taskCounts, setTaskCounts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tutor, setTutor] = useState('');
  const [student, setStudent] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [logRes, taskRes] = await Promise.all([
        ax.get('/api/admin/tutor-learning-usage', {
          params: {
            ...(tutor ? { tutor } : {}),
            ...(student ? { student } : {}),
            limit: 100,
          },
        }),
        ax.get('/api/admin/revision-tasks?limit=100'),
      ]);
      setLogs(logRes.data.logs || []);
      setTaskCounts(logRes.data.task_counts || []);
      setTasks(taskRes.data.items || []);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  }, [tutor, student]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="admin-tutor-learning-usage">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><Users className="h-5 w-5 text-red-600" /> Tutor Learning Usage</h2>
          <p className="text-xs text-gray-500 mt-0.5">Which tutors opened which topics for which students, and every revision task assigned.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 font-bold" data-testid="tlu-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl bg-sky-50 border border-sky-100 p-3">
          <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold">Total tutor accesses</div>
          <div className="text-2xl font-bold mt-1" data-testid="tlu-total-logs">{logs.length}</div>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Revision tasks total</div>
          <div className="text-2xl font-bold mt-1" data-testid="tlu-total-tasks">{tasks.length}</div>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Completed by students</div>
          <div className="text-2xl font-bold mt-1" data-testid="tlu-completed-tasks">{tasks.filter(t => t.status === 'completed').length}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1"><Filter className="h-3 w-3" /> Filter</div>
        <input value={tutor} onChange={e => setTutor(e.target.value)} placeholder="Tutor email"
          className="text-xs px-3 py-1.5 rounded-full border border-gray-200 w-56" data-testid="tlu-filter-tutor" />
        <input value={student} onChange={e => setStudent(e.target.value)} placeholder="Student email"
          className="text-xs px-3 py-1.5 rounded-full border border-gray-200 w-56" data-testid="tlu-filter-student" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Access log · latest 100</div>
          <div className="max-h-96 overflow-y-auto rounded-xl border border-gray-100 divide-y" data-testid="tlu-log-list">
            {loading && <div className="p-4 text-center text-gray-400"><Loader2 className="h-4 w-4 animate-spin inline" /></div>}
            {!loading && logs.length === 0 && <div className="p-4 text-xs text-gray-400 italic">No log entries yet.</div>}
            {logs.map((l) => (
              <div key={l.id} className="p-3 text-xs">
                <div className="font-bold text-gray-900">{l.action.replace('tutor_learning.', '')}</div>
                <div className="text-gray-600">{l.actor_email} → {l.target || '—'}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">{fmt(l.at)}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2 flex items-center gap-1"><ClipboardCheck className="h-3 w-3" /> Revision tasks · latest 100</div>
          <div className="max-h-96 overflow-y-auto rounded-xl border border-gray-100 divide-y" data-testid="tlu-task-list">
            {tasks.length === 0 && <div className="p-4 text-xs text-gray-400 italic">No revision tasks yet.</div>}
            {tasks.map((t) => (
              <div key={t.task_id} className="p-3 text-xs">
                <div className="font-bold text-gray-900 truncate">{t.resource_title || t.resource_slug}</div>
                <div className="text-gray-600">
                  {t.assigned_by} → {t.student_email} ·
                  <span className={`ml-1 inline-block px-2 py-0.5 rounded-full text-[9px] font-bold ${t.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{t.status}</span>
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">{fmt(t.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {taskCounts.length > 0 && (
        <div className="mt-4 text-[10px] text-gray-500">
          By tutor: {taskCounts.slice(0, 6).map(tc => `${(tc._id.tutor || '—').split('@')[0]}: ${tc.n} ${tc._id.status}`).join(' · ')}
        </div>
      )}
    </div>
  );
};

export default AdminTutorLearningUsagePanel;
